import { AxiosResponse } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { createGlobalState } from 'react-use';
import { useActiveProject, useService } from '../../../../hooks';
import { useCarePlans } from '../../../../hooks/useCarePlans';
import AgentsService, {
  AsyncTask,
  HealthSummaryInput,
  HealthSummaryV1Input,
  HealthSummaryV2Input,
  isTaskResolved,
} from '../../../../services/AgentsService';

const agentsService = new AgentsService();

const useTask = createGlobalState<AsyncTask<AgentInput | unknown> | undefined>(
  undefined,
);

export const useExecutiveSummaryGeneration = ({
  patientId,
  onGenerationSuccess,
  onGenerationFailure,
  onGenerationConflict,
  disableCheckForGeneratingSummary,
}: {
  patientId: string;
  onGenerationSuccess?: () => void;
  onGenerationFailure?: () => void;
  onGenerationConflict?: () => void;
  /**
   * If true, no check will be performed in the background to see
   * if a summary is already being generated for the member.
   */
  disableCheckForGeneratingSummary?: boolean;
}) => {
  const carePlanState = useCarePlans(patientId);
  const [project] = useActiveProject();

  const [listInvocations] = useService(agentsService.listInvocations);
  const [retrieveInvocation] = useService(agentsService.retrieveInvocation);
  const [assumeInvocation] = useService(agentsService.assumeInvocation, {
    rethrowErrors: true,
  });
  const [generateExecutiveSummary] = useService(
    agentsService.generateExecutiveSummary,
    { rethrowErrors: true },
  );

  const [isRefetching, setIsRefetching] = useState(false);

  const [task, setTask] = useTask();

  useEffect(() => {
    if (disableCheckForGeneratingSummary) {
      return;
    }
    // Check once if there is already an active task for this member.
    const checkForUpdates = async () => {
      const resp = await listInvocations({
        agentName: 'health-summary-agent',
        subjectId: patientId,
      });
      if (resp?.data?.length > 0) {
        setTask(resp.data[0]);
      }
    };
    checkForUpdates();
    // Only run this once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // poll the active task for completion
  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined = undefined;

    const stopInterval = () => {
      intervalId && clearInterval(intervalId);
    };

    if (!task?.id) {
      return stopInterval();
    }

    const checkForUpdates = async () => {
      const response = await retrieveInvocation({
        agentName: 'health-summary-agent',
        id: task.id,
      });
      if (!response.data || isTaskResolved(response.data)) {
        stopInterval();
        setTask(undefined);
      } else {
        setTask(response.data);
      }
      if (!response.data || response.data.status === 'completed') {
        setTimeout(() => {
          // refetch the care plan to get the new resources
          // maybe poll instead of waiting for a second?
          setIsRefetching(true);
          carePlanState.refetchCarePlans();
          onGenerationSuccess?.();
        }, 1000);
      } else if (response.data.status === 'failed') {
        onGenerationFailure?.();
      }
    };

    intervalId = setInterval(checkForUpdates, 5000);
    checkForUpdates(); // Check immediately

    return stopInterval;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retrieveInvocation, task?.id]);

  const triggerGeneration = useCallback(
    async (input: AgentInput) => {
      const agentInput = constructAgentInput({
        ...input,
        subjectId: patientId,
        projectId: project.id,
      });
      let res: AxiosResponse;
      if (task?.id) {
        res = await assumeInvocation({
          agentName: 'health-summary-agent',
          id: task.id,
        });
      } else {
        res = await generateExecutiveSummary(agentInput);
        if (res.status === 409) {
          onGenerationConflict?.();
        }
      }
      const taskId = res.headers?.location?.split('/').pop();
      if (taskId) {
        setTask({
          id: taskId,
          status: 'pending',
          created: Date.now(),
          agentInput,
        });
      }
    },
    [
      assumeInvocation,
      generateExecutiveSummary,
      patientId,
      project.id,
      task,
      setTask,
      onGenerationConflict,
    ],
  );

  return {
    task,
    isRefetching,
    generateExecutiveSummary: triggerGeneration,
    isGenerating: task?.status === 'pending' || task?.status === 'processing',
  };
};

export type AgentInput =
  | Pick<HealthSummaryV1Input, 'requestedSummaries' | 'skipActionItems'>
  | Pick<
      HealthSummaryV2Input,
      'crdId' | 'requestedSummaries' | 'skipActionItems' | 'instructions'
    >;

function constructAgentInput(
  agentInput: AgentInput & Pick<HealthSummaryInput, 'subjectId' | 'projectId'>,
): HealthSummaryInput {
  if ('crdId' in agentInput) {
    return { version: 'v2', ...agentInput };
  } else {
    return { version: 'v1', ...agentInput };
  }
}
